import { useState } from 'react';
import Avatar from './Avatar';
import Icon from './Icon';
import { absenceLabel, clock } from '../lib/schedule';
import { requestReminderSync } from '../lib/reminders';
import { useApp } from '../lib/AppContext';

const BUTTON = 'btn h-11 rounded-2xl border-line bg-base-100 font-semibold';

// What a proposal looks like on the student's side: waiting, or the coach's answer of the last day.
export function ProposalLine({ proposal }) {
  if (!proposal) return null;
  if (proposal.status === 'pending') return <p className="text-xs font-semibold text-[#8a6100]">Đã đề xuất {clock(proposal.starts_at)} · chờ Coach duyệt</p>;
  if (proposal.status === 'declined') return <p className="text-xs text-muted">Coach giữ giờ cũ{proposal.coach_note ? `: “${proposal.coach_note}”` : ''}</p>;
  if (proposal.status === 'expired') return <p className="text-xs text-muted">Đề xuất đã hết hạn, lịch giữ giờ cũ</p>;
  return null;
}

// After the session started: "Đã tập" / "Không tập", which the coach sees as the session's badge. "Đã tập"
// also logs the session as a workout when none is logged that day. The answer can be changed.
export function CheckIn({ appointment: a, onChanged, compact = false }) {
  const { api } = useApp();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  if (!a.can?.check_in) return null;
  const answer = async (attendance) => {
    setBusy(attendance);
    setError(null);
    try {
      onChanged(await api.post(`/appointments/${a.id}/attendance`, { attendance }));
      requestReminderSync();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };
  const choice = (value, label) => {
    const on = a.attendance === value;
    return (
      <button type="button" aria-pressed={on} disabled={!!busy} onClick={() => !on && answer(value)}
        className={`btn ${compact ? 'btn-sm h-9' : 'h-11'} rounded-2xl font-semibold ${on ? (value === 'trained' ? 'border-[#17804f] bg-[#17804f] text-white' : 'border-[#8e3414] bg-[#fdebe4] text-[#8e3414]') : 'border-line bg-base-100'}`}>
        {busy === value && <span className="loading loading-spinner loading-xs" />}{label}
      </button>
    );
  };
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-muted">
        {a.attendance === 'trained' ? 'Coach thấy bạn đã tập buổi này.' : a.attendance === 'missed' ? 'Coach thấy bạn không tập buổi này.' : 'Tập xong chưa? Coach sẽ thấy câu trả lời.'}
      </p>
      <div className="grid grid-cols-2 gap-2">{choice('trained', 'Đã tập')}{choice('missed', 'Không tập')}</div>
      {error && <p role="alert" className="text-xs text-error">{error}</p>}
    </div>
  );
}

// B1b: today's appointment with the coach on Today → Tập luyện, with "Xin đổi giờ" and "Báo nghỉ", then
// "Đã tập" / "Không tập" once it has started (`onCheckedIn` reloads the day, which may have a new workout).
export default function AppointmentCard({ appointment: a, onPropose, onAbsence, onChanged, onCheckedIn }) {
  const { api } = useApp();
  const [busy, setBusy] = useState(false);
  const absent = a.state === 'absent';
  const withdraw = async () => {
    setBusy(true);
    try {
      onChanged(await api.del(`/appointments/${a.id}/absence`));
      requestReminderSync();
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className={`flex flex-col gap-3 rounded-[18px] border-2 p-4 ${absent ? 'border-line bg-base-100 opacity-80' : 'border-[#17804f]/50 bg-base-100'}`}>
      <div className="flex items-center gap-3">
        <Avatar name={a.coach_name} url={a.coach_avatar_url} size={36} />
        <div className="min-w-0">
          <p className={`font-bold ${absent ? 'line-through' : ''}`}>{clock(a.starts_at)} · Tập với Coach {a.coach_name}</p>
          <p className="flex items-center gap-1 text-xs text-muted">
            {a.location && <><Icon name="target" className="size-3" />{a.location} · </>}{a.duration_min} phút
          </p>
          {a.note && <p className="text-sm font-semibold text-[#17804f]">{a.note}</p>}
        </div>
      </div>
      {absent ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm">Bạn đã báo nghỉ{a.absence.reason ? ` · ${absenceLabel(a.absence.reason)}` : ''}</p>
          {a.can?.withdraw_absence && <button type="button" className="btn btn-sm h-9 rounded-full" disabled={busy} onClick={withdraw}>Đi tập lại</button>}
        </div>
      ) : (
        <>
          <ProposalLine proposal={a.proposal} />
          {(a.can?.propose || a.can?.report_absence) && (
            <div className="grid grid-cols-2 gap-2">
              <button type="button" className={BUTTON} disabled={!a.can.propose} onClick={() => onPropose(a)}>Xin đổi giờ</button>
              <button type="button" className={BUTTON} disabled={!a.can.report_absence} onClick={() => onAbsence(a)}>Báo nghỉ</button>
            </div>
          )}
          {a.can?.check_in
            ? <CheckIn appointment={a} onChanged={(updated) => { onChanged(updated); onCheckedIn?.(); }} />
            : <p className="text-xs text-muted">Tập xong, bấm Đã tập ở đây hoặc ghi buổi tập bên dưới.</p>}
        </>
      )}
    </section>
  );
}
